import {
  instantFromIso,
  nowEpochMilliseconds,
} from "@nijiurachan/js/util/time";
import { useEffect, useRef, useState } from "react";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import {
  type AppAuthorization,
  decideAppAuthorization,
  inspectAppAuthorization,
  waitForBrowserAuthorization,
} from "@/features/app-auth/api/appAuth";
import { useAppAuthMetadata } from "@/features/app-auth/useAppAuthMetadata";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { Button } from "@/shared/ui/form";

const defaultServices = {
  inspect: inspectAppAuthorization,
  authenticate: waitForBrowserAuthorization,
  decide: decideAppAuthorization,
  navigate: (url: string): void => window.location.assign(url),
};
interface AppAuthorizationPageProps {
  requestId: string;
  services?: typeof defaultServices;
}
export const AppAuthorizationPage: React.FunctionComponent<
  AppAuthorizationPageProps
> = ({ requestId, services = defaultServices }: AppAuthorizationPageProps) => {
  useDocumentTitle(`アプリの利用を許可 - ${BOARD_DISPLAY_NAME}`);
  useAppAuthMetadata();
  const [authorization, setAuthorization] = useState<AppAuthorization | null>(
    null,
  );
  const [stage, setStage] = useState<
    "loading" | "checking" | "ready" | "submitting" | "complete" | "error"
  >("loading");
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);
  const controller = useRef<AbortController | null>(null);
  const browserToken = useRef<string | undefined>(undefined);
  const returnUrl = useRef<string | undefined>(undefined);

  // biome-ignore lint/correctness/useExhaustiveDependencies(attempt): 再試行時に認証確認を最初から実行するため意図的に含める
  useEffect(() => {
    const active = new AbortController();
    controller.current = active;
    browserToken.current = undefined;
    returnUrl.current = undefined;
    setAuthorization(null);
    setStage("loading");
    if (!/^[0-9a-f-]{36}$/i.test(requestId)) {
      setMessage(
        "認証要求が見つかりません。アプリから認証を開始してください。",
      );
      setStage("error");
      return () => active.abort();
    }
    void (async (): Promise<void> => {
      try {
        const details = await services.inspect(requestId, active.signal);
        if (active.signal.aborted) return;
        setAuthorization(details);
        setStage("checking");
        const token = await services.authenticate(active.signal);
        if (active.signal.aborted) return;
        const expiresAt = instantFromIso(details.expiresAt);
        if (
          expiresAt === null ||
          expiresAt.epochMilliseconds <= nowEpochMilliseconds()
        )
          throw new Error(
            "認証要求の有効期限が切れました。アプリからやり直してください。",
          );
        browserToken.current = token;
        setStage("ready");
      } catch (error) {
        if (active.signal.aborted) return;
        setMessage(
          error instanceof Error
            ? error.message
            : "認証を完了できませんでした。",
        );
        setStage("error");
      }
    })();
    return () => {
      active.abort();
      controller.current?.abort();
      browserToken.current = undefined;
    };
  }, [requestId, attempt, services]);

  const decide = (decision: "approve" | "deny" | "cancel"): void => {
    if (authorization === null || stage === "submitting") return;
    controller.current?.abort();
    const active = new AbortController();
    controller.current = active;
    setStage("submitting");
    void services
      .decide(
        requestId,
        authorization.csrfToken,
        decision,
        decision === "approve" ? browserToken.current : undefined,
        active.signal,
      )
      .then((result) => {
        if (active.signal.aborted) return;
        returnUrl.current = result.redirectUrl;
        setStage("complete");
        setMessage(
          decision === "approve"
            ? "許可しました。アプリへ戻って認証を完了してください。"
            : "認証を取り消しました。アプリへ戻れます。",
        );
        services.navigate(result.redirectUrl);
      })
      .catch((error: unknown) => {
        if (active.signal.aborted) return;
        setMessage(
          error instanceof Error
            ? error.message
            : "通信に失敗しました。アプリから新しい認証を開始してください。",
        );
        setStage("error");
      });
  };

  return (
    <section className="flex-1 overflow-y-auto" aria-label="アプリの利用許可">
      <div className="mx-auto max-w-xl space-y-6 px-4 py-8">
        <h1 className="text-xl font-bold">アプリの利用を許可</h1>
        {authorization && (
          <p>
            <strong>{authorization.clientName}</strong>{" "}
            に、このブラウザと同じ利用者として掲示板を利用することを許可しますか？
          </p>
        )}
        <p>
          許可すると、アプリから閲覧・投稿・添付・そうだね・delができます。投稿削除には削除キーも必要です。利用制限は引き続き適用されます。資格は最長21日間有効です。
        </p>
        <div role="status" aria-live="polite">
          {stage === "loading" && "認証要求を確認しています…"}
          {stage === "checking" &&
            "ブラウザの安全確認と認証の完了を待っています…"}
          {stage === "ready" &&
            "安全確認が完了しました。アプリ名を確認して選択してください。"}
          {stage === "submitting" && "選択を送信しています…"}
          {(stage === "error" || stage === "complete") && message}
        </div>
        {stage !== "complete" && (
          <div className="flex flex-wrap gap-3">
            <Button
              variant="primary"
              disabled={stage !== "ready"}
              onClick={() => decide("approve")}
            >
              許可する
            </Button>
            <Button
              disabled={authorization === null || stage === "submitting"}
              onClick={() => decide("deny")}
            >
              拒否する
            </Button>
            <Button
              disabled={authorization === null || stage === "submitting"}
              onClick={() => decide("cancel")}
            >
              キャンセル
            </Button>
            {stage === "error" && (
              <Button onClick={() => setAttempt((value) => value + 1)}>
                再試行
              </Button>
            )}
          </div>
        )}
        {stage === "complete" && (
          <Button
            onClick={() => {
              if (returnUrl.current) services.navigate(returnUrl.current);
            }}
          >
            アプリへ戻る
          </Button>
        )}
        <p className="text-sm text-muted-foreground">
          アプリから開始していない場合は、この画面を閉じてください。認証情報やブラウザのCookieをアプリへコピーする必要はありません。
        </p>
      </div>
    </section>
  );
};
